import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
} from '@mui/material';
import { Microsoft } from '@mui/icons-material';
import { IS_MOCK } from './useAuth';
import { useAuthContext } from './AuthContext';
import { USER_KEY } from './useMockAuth';
import type { AuthAccount } from './useAuth';
import type { SystemRole } from '../shared/types/api';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';

interface MockUser {
  id: string;
  email: string;
  displayName: string;
  systemRole: SystemRole;
  azureOid: string;
}

const ROLE_COLORS: Record<SystemRole, 'error' | 'warning' | 'primary'> = {
  SUPER_USER: 'error',
  ACCOUNT_MANAGER: 'warning',
  USER: 'primary',
};

const ROLE_LABELS: Record<SystemRole, string> = {
  SUPER_USER: 'Super User',
  ACCOUNT_MANAGER: 'Account Manager',
  USER: 'User',
};

const AVATAR_COLORS: Record<SystemRole, string> = {
  SUPER_USER: '#f56565',
  ACCOUNT_MANAGER: '#f15a24',
  USER: '#6c98e1',
};

const FEATURES = [
  'AI-powered health scoring',
  'Trend detection & analytics',
  'SharePoint report sync',
  'Compliance tracking',
  'Incremental analysis',
];

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Isometric wireframe cube grid — matches the background pattern in the reference screenshot */
function IsometricPattern() {
  const hw = 50;
  const qh = 29;
  const cubeData: Array<{ cx: number; cy: number }> = [];

  for (let row = 0; row < 9; row++) {
    const cy = row * (qh * 2) - 20;
    const isEven = row % 2 === 0;
    const startCx = isEven ? hw : 0;
    const count = isEven ? 7 : 8;
    for (let col = 0; col < count; col++) {
      cubeData.push({ cx: startCx + col * (hw * 2), cy });
    }
  }

  return (
    <Box
      component="svg"
      viewBox="0 0 700 430"
      preserveAspectRatio="xMidYMin slice"
      sx={{ width: '100%', height: '100%' }}
    >
      <g stroke="rgba(255,255,255,0.18)" strokeWidth="0.8" fill="none">
        {cubeData.map(({ cx, cy }, i) => (
          <path
            key={i}
            d={
              `M${cx},${cy} ` +
              `L${cx + hw},${cy + qh} ` +
              `L${cx + hw},${cy + qh * 3} ` +
              `L${cx},${cy + qh * 4} ` +
              `L${cx - hw},${cy + qh * 3} ` +
              `L${cx - hw},${cy + qh} Z ` +
              `M${cx + hw},${cy + qh} L${cx},${cy + qh * 2} ` +
              `M${cx - hw},${cy + qh} L${cx},${cy + qh * 2} ` +
              `M${cx},${cy + qh * 2} L${cx},${cy + qh * 4}`
            }
          />
        ))}
      </g>
    </Box>
  );
}

function MockLoginPanel() {
  const { login } = useAuthContext();
  const [users, setUsers] = useState<MockUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<MockUser[]>(endpoints.auth.mockUsers)
      .then((res) => setUsers(res.data))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  const handleSignIn = async (user: MockUser) => {
    const account: AuthAccount = {
      oid: user.azureOid,
      email: user.email,
      name: user.displayName,
      tenantId: 'mock-tenant',
      systemRole: user.systemRole,
      allowedUserId: user.id,
    };
    sessionStorage.setItem(USER_KEY, JSON.stringify(account));
    await login();
  };

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (users.length === 0) {
    return (
      <Alert severity="info">
        No users found. Add users to the AllowedUser table first.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      {users.map((user) => (
        <Box
          key={user.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.light',
              bgcolor: 'rgba(108, 152, 225, 0.04)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: AVATAR_COLORS[user.systemRole],
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'Poppins, sans-serif',
              flexShrink: 0,
            }}
          >
            {getInitials(user.displayName)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: 'text.primary' }}
            >
              {user.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          </Box>
          <Chip
            label={ROLE_LABELS[user.systemRole]}
            color={ROLE_COLORS[user.systemRole]}
            size="small"
            sx={{ flexShrink: 0, fontWeight: 500 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => void handleSignIn(user)}
            sx={{ flexShrink: 0, minWidth: 72 }}
          >
            Sign in
          </Button>
        </Box>
      ))}
    </Stack>
  );
}

function RealLoginPanel() {
  const { login } = useAuthContext();
  return (
    <Button
      variant="contained"
      size="large"
      fullWidth
      startIcon={<Microsoft />}
      onClick={() => void login()}
      sx={{ py: 1.5 }}
    >
      Sign in with Microsoft
    </Button>
  );
}

export function LoginPage() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* ── Left branding panel ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          width: '38%',
          flexShrink: 0,
          bgcolor: 'primary.dark',
          overflow: 'hidden',
        }}
      >
        {/* Top content area: logo + features + welcome */}
        <Box
          sx={{
            height: '50%',
            display: 'flex',
            flexDirection: 'column',
            px: 5,
            pt: 4.5,
            pb: 3,
          }}
        >
          {/* V2 Solutions logo */}
          <Box
            component="img"
            src="https://www.v2solutions.com/wp-content/uploads/2024/02/V2-logo.svg"
            alt="V2 Solutions"
            sx={{
              marginLeft: '-30px', marginTop: '-8px',
              height: 38,
              objectFit: 'contain',
              objectPosition: 'left center',
              filter: 'brightness(0) invert(1)',
            }}
          />

          {/* Push features to the bottom of the top half */}
          <Box sx={{ flex: 1 }} />

          {/* Feature list */}
          <Box sx={{ mb: 3 }}>
            {FEATURES.map((feat) => (
              <Typography
                key={feat}
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '1.25rem',
                  lineHeight: 1.75,
                  color: 'rgba(255,255,255,0.88)',
                }}
              >
                {"- "} {feat}
              </Typography>
            ))}
          </Box>

          {/* Welcome text */}
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              fontSize: '1.65rem',
              lineHeight: 1.3,
              color: 'white',
            }}
          >
            Welcome to
            <br />
            Beacon.
          </Typography>
        </Box>

        {/* Bottom pattern area */}
        <Box sx={{ height: '50%', overflow: 'hidden' }}>
          <IsometricPattern />
        </Box>
      </Box>

      {/* ── Right login panel ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, sm: 6, md: 8 },
          py: 6,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          {/* Mobile-only logo */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              mb: 4,
            }}
          >
            <Box
              component="img"
              src="https://www.v2solutions.com/wp-content/uploads/2024/02/V2-logo.svg"
              alt="V2 Solutions"
              sx={{ height: 32, objectFit: 'contain' }}
            />
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              color: 'primary.main',
              mb: 1,
            }}
          >
            Welcome back
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {IS_MOCK
              ? 'Development mode — select an account to continue'
              : 'Sign in with your Microsoft account to continue'}
          </Typography>

          {IS_MOCK ? <MockLoginPanel /> : <RealLoginPanel />}
        </Box>
      </Box>
    </Box>
  );
}
